import { useEffect } from 'react';
import { LoadData, LoadSliderInputRanges, LoadSliderInputValues, MesRequest, Range, SliderInputValue } from '../../../models';
import { setLoadSliderRanges, setLoadSliderTexts, setMesRequest, setTags, toggleLoading } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../common';
import { calculateM0Range, calculateM1Range, calculateM2Range, calculateM3Range, calculateM4Range, calculateN0Range, calculateN1Range, calculateN2Range, calculateN3Range, calculateN4Range, getClosestRangeRound, getDefaultLoadData, getDefaultStartingVoltage, getNw, isNullOrUndefined, mapToSliderInputText } from '../../../services';
import { LoadType } from '../../../enums';
import { useHook } from '../../Hooks';

export const useLoadData = () => {
    const dispatch = useAppDispatch();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const loadSliderTexts = useAppSelector(state => state.load.loadSliderTexts);
    const loadSliderRanges = useAppSelector(state => state.load.loadSliderRanges);
    const updateFilters = useAppSelector(state => state.motor.updateFilters);
    const { getCalculationData } = useHook(x => x.filter)();

    useEffect(() => {
        calculateLoadTextAndRange();
    }, [updateFilters]);

    const calculateLoadTextAndRange = async (loadData?: LoadData) => {
        const { m0, m1, m2, m3, m4, n0, n1, n2, n3, n4, loadType } = loadData || mesRequest.loadData;
        const { ma, mn, nN, nsyn } = mesRequest.commonData;
        const nw = Number(getNw(mesRequest.commonData));
        const maAsNumber = Number(ma);
        const mnAsNumber = Number(mn);
        const nNAsNumber = Number(nN);
        const nsynAsNumber = Number(nsyn);
        const n0AsNumber = Number(n0);
        const n1AsNumber = Number(n1);
        const n2AsNumber = Number(n2);
        const n3AsNumber = Number(n3);
        const n4AsNumber = Number(n4);
        const m0AsNumber = Number(m0);
        const m1AsNumber = Number(m1);
        const m2AsNumber = Number(m2);
        const m3AsNumber = Number(m3);
        const m4AsNumber = Number(m4);

        const loadSliderInputRanges: LoadSliderInputRanges = {
            n0: isNullOrUndefined(n0) ? null : calculateN0Range(loadType),
            n1: isNullOrUndefined(n1) ? null : calculateN1Range(loadType, nsynAsNumber, nw, nNAsNumber, n1AsNumber),
            n2: isNullOrUndefined(n2) ? null : calculateN2Range(loadType, nNAsNumber, n1AsNumber, n2AsNumber),
            n3: isNullOrUndefined(n3) ? null : calculateN3Range(loadType, nNAsNumber, n2AsNumber, n3AsNumber),
            n4: isNullOrUndefined(n4) ? null : calculateN4Range(loadType, nsynAsNumber, nNAsNumber, n4AsNumber),
            m0: isNullOrUndefined(m0) ? null : calculateM0Range(loadType, maAsNumber, m0AsNumber),
            m1: isNullOrUndefined(m1) ? null : calculateM1Range(loadType, maAsNumber, mnAsNumber, m1AsNumber),
            m2: isNullOrUndefined(m2) ? null : calculateM2Range(loadType, mnAsNumber, m2AsNumber),
            m3: isNullOrUndefined(m3) ? null : calculateM3Range(loadType, mnAsNumber, m3AsNumber),
            m4: isNullOrUndefined(m4) ? null : calculateM4Range(loadType, maAsNumber, mnAsNumber, m0AsNumber, m1AsNumber, nNAsNumber, n1AsNumber, n4AsNumber, nsynAsNumber, m4AsNumber)
        };

        dispatch(setLoadSliderRanges(loadSliderInputRanges));

        dispatch(setLoadSliderTexts({
            n0: loadSliderInputRanges.n0 && mapToSliderInputText(n0AsNumber),
            n1: loadSliderInputRanges.n1 && mapToSliderInputText(n1AsNumber),
            n2: loadSliderInputRanges.n2 && mapToSliderInputText(n2AsNumber),
            n3: loadSliderInputRanges.n3 && mapToSliderInputText(n3AsNumber),
            n4: loadSliderInputRanges.n4 && mapToSliderInputText(n4AsNumber),
            m0: loadSliderInputRanges.m0 && mapToSliderInputText(m0AsNumber),
            m1: loadSliderInputRanges.m1 && mapToSliderInputText(m1AsNumber),
            m2: loadSliderInputRanges.m2 && mapToSliderInputText(m2AsNumber),
            m3: loadSliderInputRanges.m3 && mapToSliderInputText(m3AsNumber),
            m4: loadSliderInputRanges.m4 && mapToSliderInputText(m4AsNumber)
        }));
    };
        
    const setStartingVoltage = (mesRequest: MesRequest) => {
        const newStartingVoltage = getDefaultStartingVoltage(mesRequest.loadData.loadType);
        const newMesRequest = { ...mesRequest, commonData: { ...mesRequest.commonData, startingVoltage: newStartingVoltage } };

        dispatch(setTags({ startingVoltage: undefined }));

        dispatch(setMesRequest(newMesRequest));

        return newMesRequest;
    };

    const handleLoadChange = async (loadData: Partial<LoadData>) => {
        dispatch(toggleLoading());

        let newMesRequest = { ...mesRequest, loadData: { ...mesRequest.loadData, ...loadData } };
        const previousLoadType = mesRequest.loadData.loadType;
        const currentLoadType = newMesRequest.loadData.loadType;

        dispatch(setMesRequest(newMesRequest));

        if ((previousLoadType !== LoadType.Custom && currentLoadType === LoadType.Custom) || (previousLoadType === LoadType.Custom && currentLoadType !== LoadType.Custom)) {
            newMesRequest = setStartingVoltage(newMesRequest);
        }

        await getCalculationData(newMesRequest);

        dispatch(toggleLoading());
    };

    const changeRange = (newRange: Range, property: keyof LoadSliderInputValues, subset?: Partial<LoadData>) => {
        const value = mesRequest.loadData[property] as number;
        const lessThanMin = value < newRange.min;

        if (lessThanMin || value > newRange.max) {
            const correctValue = lessThanMin ? newRange.min : newRange.max;

            if (subset) {
                subset[property] = correctValue;
            }

            dispatch(setLoadSliderTexts({ [property]: mapToSliderInputText(correctValue) }));
            dispatch(setLoadSliderRanges({ [property]: newRange }));
            dispatch(setTags({ [property]: undefined }));
        } else {
            dispatch(setLoadSliderTexts({
                [property]: {
                    ...loadSliderTexts[property],
                    sliderValue: getClosestRangeRound(value, newRange.min, newRange.step)
                }
            }));
            dispatch(setLoadSliderRanges({ [property]: newRange }));
        }
    };

    const changeM4Range = (m4Range: Range, subset?: Partial<LoadData>) => changeRange(m4Range, 'm4', subset);

    const handleN1SliderChange = (value: number | null) => {
        const { ma, mn, nN, nsyn } = mesRequest.commonData;
        const { loadType, n4, m0, m1 } = mesRequest.loadData;
        const subset: Partial<LoadData> = { n1: value };

        if (loadType === LoadType.QuadraticWithBreakaway && Number(n4) < Number(nN)) {
            const newM4Range = calculateM4Range(loadType, Number(ma), Number(mn), Number(m0), Number(m1), Number(nN), Number(subset.n1), Number(n4), Number(nsyn));

            changeM4Range(newM4Range, subset);
        }

        if (loadType === LoadType.Custom) {
            const newN2Range = calculateN2Range(loadType, Number(nN), Number(subset.n1)) as Range;

            changeRange(newN2Range, 'n2', subset);

            if (!isNullOrUndefined(subset.n2)) {
                const newN3Range = calculateN3Range(loadType, Number(nN), Number(subset.n2));

                changeRange(newN3Range, 'n3', subset);
            }
        }

        handleLoadChange(subset);
    };

    const handleN2SliderInputChange = (value: number) => {
        const { nN } = mesRequest.commonData;
        const { loadType } = mesRequest.loadData;
        const subset: Partial<LoadData> = { n2: value };

        if (loadType === LoadType.Custom) {
            const newN3Range = calculateN3Range(loadType, Number(nN), Number(subset.n2));

            changeRange(newN3Range, 'n3', subset);
        }

        handleLoadChange(subset);
    };

    const handleN3SliderInputChange = (value: number) => handleLoadChange({ n3: value });

    const handleN4SliderInputChange = (value: number) => {
        const { ma, mn, nN, nsyn } = mesRequest.commonData;
        const { loadType, n1, m0, m1 } = mesRequest.loadData;
        const subset: Partial<LoadData> = { n4: value };
        const newM4Range = calculateM4Range(loadType, Number(ma), Number(mn), Number(m0), Number(m1), Number(nN), Number(n1), Number(subset.n4), Number(nsyn));

        changeM4Range(newM4Range, subset);

        handleLoadChange(subset);
    };

    const handleM0SliderInputChange = (value: number) => {
        const { ma, mn, nN, nsyn } = mesRequest.commonData;
        const { loadType, n1, n4, m1 } = mesRequest.loadData;
        const subset: Partial<LoadData> = { m0: value };

        if (loadType === LoadType.Linear && Number(n4) < Number(nN)) {
            const newM4Range = calculateM4Range(loadType, Number(ma), Number(mn), Number(subset.m0), Number(m1), Number(nN), Number(n1), Number(n4), Number(nsyn));

            changeM4Range(newM4Range, subset);
        }

        handleLoadChange(subset);
    };

    const handleM1SliderChange = (subset: Partial<LoadData>, newMesRequest?: MesRequest) => {
        const { loadType, n1, n4, m0 } = newMesRequest?.loadData ?? mesRequest.loadData;
        const { ma, mn, nN, nsyn } = newMesRequest?.commonData ?? mesRequest.commonData;

        if (loadType === LoadType.QuadraticWithBreakaway && Number(n4) < Number(nN)) {
            const newM4Range = calculateM4Range(loadType, Number(ma), Number(mn), Number(m0), Number(subset.m1), Number(nN), Number(n1), Number(n4), Number(nsyn));

            changeM4Range(newM4Range, subset);
        }

        handleLoadChange({ ...newMesRequest?.loadData, ...subset });
    };

    const handleM2SliderChange = (value: number) => handleLoadChange({ m2: value });

    const handleM3SliderChange = (value: number) => handleLoadChange({ m3: value });

    const handleM4SliderChange = (value: number) => handleLoadChange({ m4: value });

    const setCustomLoadPointsTorqueDefaultValues = (property: keyof LoadSliderInputValues, mesRequest: MesRequest) => {
        const { m1, m2, m3 } = loadSliderRanges;
        const value = loadSliderTexts[property] as SliderInputValue;

        if (loadSliderTexts) {
            const m1InputValue = m1 && Number(value.inputValue) > m1?.max ? m1.max.toString() : value.inputValue;
            const m2InputValue = m2 && Number(value.inputValue) > m2?.max ? m2.max.toString() : value.inputValue;
            const m3InputValue = m3 && Number(value.inputValue) > m3?.max ? m3.max.toString() : value.inputValue;
            const m1SliderValue = m1 && Number(value.sliderValue) > m1?.max ? m1.max : value.sliderValue;
            const m2SliderValue = m2 && Number(value.sliderValue) > m2?.max ? m2.max : value.sliderValue;
            const m3SliderValue = m3 && Number(value.sliderValue) > m3?.max ? m3.max : value.sliderValue;

            if (property === 'm0') {
                dispatch(setLoadSliderTexts({
                    m1: { inputValue: m1InputValue, sliderValue: m1SliderValue },
                    m2: { inputValue: m2InputValue, sliderValue: m2SliderValue },
                    m3: { inputValue: m3InputValue, sliderValue: m3SliderValue }
                }));

                handleM1SliderChange({ m1: Number(m1InputValue), m2: Number(m2InputValue), m3: Number(m3InputValue) }, mesRequest);
            } else if (property === 'm1') {
                dispatch(setLoadSliderTexts({
                    m2: { inputValue: m2InputValue, sliderValue: m2SliderValue },
                    m3: { inputValue: m3InputValue, sliderValue: m3SliderValue }
                }));

                handleLoadChange({ ...mesRequest.loadData, m2: Number(m2InputValue), m3: Number(m3InputValue) });
            } else if (property === 'm2') {
                dispatch(setLoadSliderTexts({
                    m3: { inputValue: m3InputValue, sliderValue: m3SliderValue }
                }));

                handleLoadChange({ ...mesRequest.loadData, m3: Number(m3InputValue) });
            }
        }
    };

    const getChangeHandlerForProperty = (property: keyof LoadSliderInputValues, value: number) => {
        switch (property) {
            case 'n1':
                handleN1SliderChange(value);
                break;
            case 'n2':
                handleN2SliderInputChange(value);
                break;
            case 'n3':
                handleN3SliderInputChange(value);
                break;
            case 'n4':
                handleN4SliderInputChange(value);
                break;
            case 'm0':
                handleM0SliderInputChange(value);
                break;
            case 'm1':
                handleM1SliderChange({ m1: value });
                break;
            case 'm2':
                handleM2SliderChange(value);
                break;
            case 'm3':
                handleM3SliderChange(value);
                break;
            case 'm4':
                handleM4SliderChange(value);
                break;
        }
    };


    const handleNMTagClick = (
        property: keyof LoadSliderInputRanges,
        handleOnSliderInputChange: (value: number) => void
    ) => {
        let defaultValue = getDefaultLoadData(mesRequest.loadData.loadType, mesRequest)[property] ?? 0;
        const { min, max } = loadSliderRanges[property] as Range;

        const lessThanMin = defaultValue < min;

        if (lessThanMin || defaultValue > max) {
            defaultValue = lessThanMin ? min : max;
        }

        dispatch(setLoadSliderTexts({ [property]: mapToSliderInputText(defaultValue) }));
        dispatch(setTags({ [property]: undefined }));

        handleOnSliderInputChange(defaultValue);
    };

    return {
        changeRange,
        calculateLoadTextAndRange,
        onLoadChange: handleLoadChange,
        onNMTagClick: handleNMTagClick,
        getChangeHandlerForProperty,
        setCustomLoadPointsTorqueDefaultValues,
        onN1SliderChange: handleN1SliderChange,
        onN2SliderInputChange: handleN2SliderInputChange,
        onN3SliderInputChange: handleN3SliderInputChange,
        onN4SliderInputChange: handleN4SliderInputChange,
        onM0SliderInputChange: handleM0SliderInputChange,
        onM1SliderChange: handleM1SliderChange,
        onM2SliderChange: handleM2SliderChange,
        onM3SliderChange: handleM3SliderChange,
        onM4SliderChange: handleM4SliderChange
    };
};
